import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Settings as SettingsIcon, Save, PieChart, Moon, CreditCard, Mail, ArrowRight } from "lucide-react";
import { getStoredTheme, applyTheme } from "../lib/theme";
import { Link } from "react-router-dom";

const PRESETS = ["多谢惠顾，好运连连", "感谢您的信任，福缘常伴", "Thank you for your purchase!"];

function SectionSaveBar({ dirty, saving, onSave }) {
    return (
        <div className="flex items-center gap-3 pt-2">
            <Button
                onClick={onSave}
                disabled={saving || !dirty}
                className="bg-gold-600 hover:bg-gold-700 text-white gap-2"
            >
                <Save className="h-4 w-4" /> {saving ? "保存中..." : "保存"}
            </Button>
            {dirty && <span className="text-xs text-amber-600">有未保存的修改</span>}
        </div>
    );
}

export default function SettingsPage() {
    const [loading, setLoading] = useState(true);

    // Section 1: invoice footer
    const [note, setNote] = useState("");
    const [initialNote, setInitialNote] = useState("");
    const [savingNote, setSavingNote] = useState(false);

    // Section 2: payout chart
    const [showChart, setShowChart] = useState(true);
    const [initialShowChart, setInitialShowChart] = useState(true);
    const [savingChart, setSavingChart] = useState(false);

    // Section 3: theme (device-local)
    const [theme, setTheme] = useState(getStoredTheme());

    useEffect(() => {
        api.get("/settings").then(({ data }) => {
            setNote(data.invoice_footer_note || "");
            setInitialNote(data.invoice_footer_note || "");
            setShowChart(data.show_payout_chart !== false);
            setInitialShowChart(data.show_payout_chart !== false);
            setLoading(false);
        });
    }, []);

    const saveNote = async () => {
        setSavingNote(true);
        try {
            await api.put("/settings", { invoice_footer_note: note, show_payout_chart: showChart });
            setInitialNote(note);
            toast.success("发票标语已保存");
        } catch (err) {
            toast.error(err.response?.data?.detail || "保存失败");
        } finally { setSavingNote(false); }
    };

    const saveChart = async () => {
        setSavingChart(true);
        try {
            await api.put("/settings", { invoice_footer_note: initialNote, show_payout_chart: showChart });
            setInitialShowChart(showChart);
            toast.success("显示偏好已保存");
        } catch (err) {
            toast.error(err.response?.data?.detail || "保存失败");
        } finally { setSavingChart(false); }
    };

    const toggleTheme = (dark) => {
        const next = dark ? "dark" : "light";
        setTheme(next);
        applyTheme(next);
        toast.success(dark ? "已切换深色模式" : "已切换浅色模式");
    };

    if (loading) return <div className="text-gold-500">加载中...</div>;

    return (
        <div className="space-y-6" data-testid="settings-page">
            <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">系统设置</div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">常规设置</h1>
                <p className="text-sm text-gold-500 mt-1">自定义发票、显示偏好与外观 · Stripe / 邮件通知已单独放到侧栏</p>
            </div>

            {/* Quick links to split-out settings pages */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
                <Link
                    to="/settings/payment"
                    data-testid="link-payment-settings"
                    className="group flex items-center gap-3 p-4 rounded-lg border border-slate-200 bg-white hover:border-gold-400 hover:bg-gold-50/40 transition-colors"
                >
                    <div className="h-10 w-10 rounded-md bg-gold-50 flex items-center justify-center shrink-0">
                        <CreditCard className="h-5 w-5 text-gold-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">Stripe 收款</div>
                        <div className="text-xs text-gold-500 mt-0.5 truncate">Publishable / Secret key · 在线付款开关</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-gold-600 group-hover:translate-x-0.5 transition-all" />
                </Link>
                <Link
                    to="/settings/email"
                    data-testid="link-email-settings"
                    className="group flex items-center gap-3 p-4 rounded-lg border border-slate-200 bg-white hover:border-gold-400 hover:bg-gold-50/40 transition-colors"
                >
                    <div className="h-10 w-10 rounded-md bg-gold-50 flex items-center justify-center shrink-0">
                        <Mail className="h-5 w-5 text-gold-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">邮件通知</div>
                        <div className="text-xs text-gold-500 mt-0.5 truncate">SMTP · 主题 · 内部通知 & 客户确认信</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-gold-600 group-hover:translate-x-0.5 transition-all" />
                </Link>
            </div>

            {/* 1. Invoice footer */}
            <Card className="p-6 border-slate-200 shadow-sm max-w-3xl">
                <div className="flex items-start gap-3 mb-6">
                    <div className="h-9 w-9 rounded-md bg-gold-50 flex items-center justify-center shrink-0">
                        <SettingsIcon className="h-4 w-4 text-gold-600" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold tracking-tight text-slate-900">发票底部标语</h2>
                        <p className="text-xs text-gold-500 mt-0.5">显示在每张导出 PDF 发票底部</p>
                    </div>
                </div>
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label>标语内容</Label>
                        <Textarea data-testid="invoice-footer-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="例如：多谢惠顾，好运连连" maxLength={200} />
                        <div className="flex items-center justify-between text-xs">
                            <div className="text-gold-500">留空则用默认「感谢惠顾 · Thank you for your purchase」</div>
                            <div className="font-mono text-slate-400">{note.length}/200</div>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs text-gold-500">快速选择</Label>
                        <div className="flex flex-wrap gap-2">
                            {PRESETS.map((p) => (
                                <button key={p} type="button" onClick={() => setNote(p)} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-gold-400 hover:bg-gold-50 hover:text-gold-700 text-slate-700 transition-colors">
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="rounded-md border border-dashed border-slate-300 bg-gold-50 p-4">
                        <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">发票底部预览</div>
                        <div className="flex justify-between text-xs text-gold-500 pt-3 border-t border-slate-200">
                            <span>{note.trim() || "感谢惠顾 · Thank you for your purchase"}</span>
                            <span>FuYuan Jewelry · 福缘珠宝</span>
                        </div>
                    </div>
                    <SectionSaveBar dirty={note !== initialNote} saving={savingNote} onSave={saveNote} />
                </div>
            </Card>

            {/* 2. Payout chart toggle */}
            <Card className="p-6 border-slate-200 shadow-sm max-w-3xl">
                <div className="flex items-start gap-3 mb-4">
                    <div className="h-9 w-9 rounded-md bg-gold-50 flex items-center justify-center shrink-0">
                        <PieChart className="h-4 w-4 text-gold-600" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold tracking-tight text-slate-900">显示偏好</h2>
                        <p className="text-xs text-gold-500 mt-0.5">控制页面上哪些图表可见</p>
                    </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-gold-50">
                    <div>
                        <div className="text-sm font-medium text-slate-900">分成记录 · 各对象饼图</div>
                        <div className="text-xs text-gold-500 mt-0.5">关闭后分成记录页不再显示饼图</div>
                    </div>
                    <Switch data-testid="toggle-payout-chart" checked={showChart} onCheckedChange={setShowChart} />
                </div>
                <SectionSaveBar dirty={showChart !== initialShowChart} saving={savingChart} onSave={saveChart} />
            </Card>

            {/* 3. Dark mode */}
            <Card className="p-6 border-slate-200 shadow-sm max-w-3xl">
                <div className="flex items-start gap-3 mb-4">
                    <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                        <Moon className="h-4 w-4 text-slate-700" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold tracking-tight text-slate-900">外观</h2>
                        <p className="text-xs text-gold-500 mt-0.5">仅本设备生效，切换立即应用</p>
                    </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-gold-50">
                    <div>
                        <div className="text-sm font-medium text-slate-900">深色模式</div>
                        <div className="text-xs text-gold-500 mt-0.5">晚上录单更护眼</div>
                    </div>
                    <Switch data-testid="toggle-dark-mode" checked={theme === "dark"} onCheckedChange={toggleTheme} />
                </div>
            </Card>
        </div>
    );
}
